import { useEffect, useState } from 'react'
import { useThree } from '@react-three/fiber'
import type { Mesh, Object3D } from 'three'
import { useContorno } from '../ambiente/registroPosProcessamento'
import { Rotulo3D } from '../componentes/Rotulo3D'

/**
 * Destaques de inspeção da colisão — moderados de propósito.
 *
 * - Marcador do 1º contato, fixo no ponto do mundo em que ele aconteceu.
 * - Contorno das peças deformadas (desenhado pelo `PosProcessamento`; aqui só se
 *   publicam as malhas no canal `deformadas`), só com a timeline PAUSADA
 *   e o mapa de deformação ligado: é o momento em que o professor aponta e
 *   explica. Durante a reprodução, nada de efeito extra.
 *
 * As peças deformadas são reconhecidas pela marca `userData.deformado` que
 * `useGeometriaDeformada` põe no clone — o veículo intacto nunca é contornado.
 */
export function DestaqueImpacto3D({
  pontoContato,
  contornar,
  chave,
}: {
  pontoContato: [number, number, number] | null
  contornar: boolean
  /** muda quando a deformação muda; recoleta as malhas */
  chave: string
}) {
  return (
    <group>
      {pontoContato && <MarcadorContato posicao={pontoContato} />}
      {contornar && <ContornoDeformadas chave={chave} />}
    </group>
  )
}

function MarcadorContato({ posicao }: { posicao: [number, number, number] }) {
  const [x, , z] = posicao
  return (
    <group position={[x, 0.012, z]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} renderOrder={8}>
        <ringGeometry args={[0.16, 0.22, 32]} />
        <meshBasicMaterial color="#ef4444" transparent opacity={0.85} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0.55, 0]}>
        <cylinderGeometry args={[0.008, 0.008, 1.1, 6]} />
        <meshBasicMaterial color="#ef4444" transparent opacity={0.6} />
      </mesh>
      <Rotulo3D posicao={[0, 1.25, 0]} texto="1º contato" variante="risco" fator={5} sempreVisivel />
    </group>
  )
}

function coletarDeformadas(raiz: Object3D): Mesh[] {
  const malhas: Mesh[] = []
  raiz.traverse((objeto) => {
    const malha = objeto as Mesh
    if (malha.isMesh && malha.geometry?.userData?.deformado) malhas.push(malha)
  })
  return malhas
}

function ContornoDeformadas({ chave }: { chave: string }) {
  const scene = useThree((estado) => estado.scene)
  const [malhas, setMalhas] = useState<Mesh[]>([])
  useEffect(() => {
    // um tique depois do commit: as malhas deformadas acabaram de montar
    const id = setTimeout(() => setMalhas(coletarDeformadas(scene)), 30)
    return () => clearTimeout(id)
  }, [scene, chave])
  useContorno('deformadas', malhas)
  return null
}
